import { z } from 'zod';

/**
 * Per-team AI feature → model override DTOs.  Phase 1 follow-up over the
 * YAML default in projoint-feature-models.yaml.
 *
 * Validation mirrors the backend (AiFeatureOverridesService.FEATURES +
 * CATALOG_MODELS).  Keep them in lockstep — adding a new feature on
 * either side requires bumping both lists + the migration's CHECK
 * constraint (latest: 0123).
 *
 * `plan_day`, `weekly_review`, `standup_draft` are the W6 personal AI
 * features: they run only on the Personal team, so only a Personal team's
 * catalog lists them (the server refuses them for a regular team).
 */

export const AiFeatureName = z.enum([
  'parse-task',
  'digest',
  'copilot-assignee',
  'copilot-tags',
  'copilot-description',
  'smart-reply',
  'summarize-thread',
  'comment-improver',
  'assistant-chat',
  'plan_day',
  'weekly_review',
  'standup_draft',
]);
export type AiFeatureNameValue = z.infer<typeof AiFeatureName>;

export const AiCatalogModelId = z.enum([
  'anthropic/claude-haiku-4-5',
  'anthropic/claude-sonnet-4-6',
  'openai/gpt-4o-mini',
  'openai/gpt-4o',
  // Same models served through OpenRouter (one OpenRouter key covers all).
  'openrouter/anthropic/claude-haiku-4.5',
  'openrouter/anthropic/claude-sonnet-4.6',
  'openrouter/openai/gpt-4o-mini',
  'openrouter/openai/gpt-4o',
]);
export type AiCatalogModelIdValue = z.infer<typeof AiCatalogModelId>;

export const UpsertAiFeatureOverrideSchema = z.object({
  feature: AiFeatureName,
  modelId: AiCatalogModelId,
  fallbackModelId: AiCatalogModelId.nullable().optional(),
});
export type UpsertAiFeatureOverrideInput = z.infer<typeof UpsertAiFeatureOverrideSchema>;

export const AiFeatureOverrideSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  feature: AiFeatureName,
  modelId: z.string(),
  fallbackModelId: z.string().nullable(),
  updatedBy: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AiFeatureOverrideProjection = z.infer<typeof AiFeatureOverrideSchema>;

export const AiFeatureOverrideListResponseSchema = z.object({
  items: z.array(AiFeatureOverrideSchema),
  /** Echo the catalog + feature lists so the UI doesn't hard-code them. */
  catalog: z.object({
    features: z.array(AiFeatureName),
    models: z.array(AiCatalogModelId),
  }),
});
export type AiFeatureOverrideListResponse = z.infer<typeof AiFeatureOverrideListResponseSchema>;
